import { useCallback } from 'react';
import { useQueryClient, useMutation } from 'react-query';
import { requestCardMutationFns as mutationFns } from '../helpers/requestCardMutationFns';

const invalidateRequestQuery = ({ queryClient, queryKey = 'request', value }) =>
  queryClient.invalidateQueries([queryKey, value]);

const getStandartOptions = ({
  queryClient,
  queryKey = 'request',
  valueKey = 'request',
}) => ({
  onSuccess: (_data, { values }) => {
    const value = values[valueKey];

    const requestId = typeof value === 'number' ? value : Number(value);

    return invalidateRequestQuery({ queryClient, queryKey, value: requestId });
  },
});

const getWarrantyDocsOptions = (queryClient) => ({
  onSuccess: (_data, { requestId }) =>
    queryClient.invalidateQueries(['warrantyDocs', Number(requestId)]),
});

const useRequestCardMutationFns = () => {
  const queryClient = useQueryClient();

  const masterMutation = useMutation(
    mutationFns.master,
    getStandartOptions({ queryClient })
  );

  const executerMutation = useMutation(
    mutationFns.executer,
    getStandartOptions({ queryClient })
  );

  const addressMutation = useMutation(
    mutationFns.address,
    getStandartOptions({ queryClient })
  );

  const serviceMutation = useMutation(
    mutationFns.service,
    getStandartOptions({ queryClient })
  );

  const executionTimeMutation = useMutation(
    mutationFns.executionTime,
    getStandartOptions({ queryClient })
  );

  const warrantyInfoMutation = useMutation(
    mutationFns.warrantyInfo,
    getStandartOptions({
      queryClient,
      queryKey: 'contractor',
      valueKey: 'requestId',
    })
  );

  const warrantyDocsMutation = useMutation(
    mutationFns.warrantyDocs,
    getWarrantyDocsOptions(queryClient)
  );

  const calendarEventAddMutation = useMutation(
    mutationFns.calendarEventAdd,
    getStandartOptions({ queryClient })
  );

  const calendarEventUpdateMutation = useMutation(
    mutationFns.calendarEventUpdate,
    getStandartOptions({ queryClient })
  );

  const calendarEventDeleteMutation = useMutation(
    mutationFns.calendarEventDelete,
    getStandartOptions({ queryClient })
  );

  const handleMasterChange = useCallback(
    (values) => masterMutation.mutateAsync({ values }),
    []
  );

  const handleExecuterChange = useCallback(
    (values) => executerMutation.mutateAsync({ values }),
    []
  );

  const handleAddressChange = useCallback(
    (values) => addressMutation.mutateAsync({ values }),
    []
  );

  const handleServiceChange = useCallback(
    (values) => serviceMutation.mutateAsync({ values }),
    []
  );

  const handleExecutionTimeChange = useCallback(
    (values) => executionTimeMutation.mutateAsync({ values }),
    []
  );

  const handleWarrantyInfoChange = useCallback(
    (values) => warrantyInfoMutation.mutateAsync({ values }),
    []
  );

  const handleWarrantyDocsAdd = useCallback(
    (requestId, values) =>
      warrantyDocsMutation.mutateAsync({ requestId, values }),
    []
  );

  const handleCalendarEventChange = useCallback(
    (values) =>
      values.eventId
        ? calendarEventUpdateMutation.mutateAsync({ values })
        : calendarEventAddMutation.mutateAsync({ values }),
    []
  );

  const handleCalendarEventDelete = useCallback(
    (values) => calendarEventDeleteMutation.mutateAsync({ values }),
    []
  );

  return {
    handleMasterChange,
    handleExecuterChange,
    handleAddressChange,
    handleServiceChange,
    handleExecutionTimeChange,
    handleWarrantyInfoChange,
    handleWarrantyDocsAdd,
    handleCalendarEventChange,
    handleCalendarEventDelete,
  };
};

export default useRequestCardMutationFns;
